import React, { useState } from "react";

import {
  ChevronUpIcon as ChevronUp,
  ChevronDownIcon as ChevronDown
} from "@packages/ds";
import moment from "moment";
import { useIntl } from "react-intl";

import { Tooltip } from "ds/Tooltip";
import { selectServicesProcessesFiltered } from "Reducers/serviceProcesses/serviceProcesses.selectors";
import { OrderBy } from "Reducers/types";
import { useAppSelector } from "Store/hooks";

import { OptionsButton } from "../components/OptionsButton";
import { convertBytesToMaxUnit } from "../utils/convertBytesToMaxUnit";
import { convertDateStringToHuman } from "../utils/convertDateStringToHuman";
import { headersConfig, HeadersKeys } from "../utils/headersConfig";

import * as S from "./TableList.styles";

export type TableListProps = {
  search: string;
  orderBy: OrderBy<HeadersKeys>;
  setOrderBy: (orderBy: OrderBy<HeadersKeys>) => void;
};

export const TableList = ({ search, orderBy, setOrderBy }: TableListProps) => {
  const { formatMessage } = useIntl();
  const [currentOpen, setCurrentOpen] = useState(0);
  const serviceProcesses = useAppSelector(state =>
    selectServicesProcessesFiltered(state, {
      orderBy,
      search
    })
  );

  return (
    <S.TableListRoot>
      <thead>
        <tr>
          {headersConfig.map(header => (
            <th key={header.key}>
              <S.SortByHeader
                onClick={() => {
                  setOrderBy(
                    orderBy.field === header.key
                      ? {
                          direction: -orderBy.direction as -1,
                          field: orderBy.field
                        }
                      : {
                          direction: 1,
                          field: header.key
                        }
                  );
                }}
              >
                <div>{formatMessage({ id: header.label })}</div>

                {orderBy.field !== header.key && (
                  <ChevronDown color="#C9D0E4" />
                )}

                {orderBy.field === header.key && orderBy.direction === 1 && (
                  <ChevronDown color="#4786FF" />
                )}

                {orderBy.field === header.key && orderBy.direction === -1 && (
                  <ChevronUp color="#4786FF" />
                )}
              </S.SortByHeader>
            </th>
          ))}
          <th />
        </tr>
      </thead>

      <tbody>
        {serviceProcesses.map(serviceProcess => (
          <tr
            key={serviceProcess.pid}
            className={serviceProcess.pid === currentOpen ? "active" : ""}
          >
            <td>{serviceProcess.name || "-"}</td>
            <td>{serviceProcess.pid}</td>
            <td>{serviceProcess.parent_pid || "-"}</td>
            <td>{serviceProcess.children_number}</td>
            <td className={serviceProcess.status === "D" ? "d-status" : ""}>
              {serviceProcess.status || "-"}
            </td>
            <td>{serviceProcess.cpu_percent.toFixed(1)}</td>
            <td>{convertBytesToMaxUnit(serviceProcess.rss)}</td>
            <td>{serviceProcess.username || "-"}</td>
            <td>
              <Tooltip
                align="end"
                sideOffset={8}
                tooltip={formatMessage(
                  {
                    id: "services.process.colum.run_tooltip"
                  },
                  {
                    date: moment(serviceProcess.create_time).format(
                      "HH:mm:ss MMMM D YYYY"
                    )
                  }
                )}
              >
                {convertDateStringToHuman(serviceProcess.create_time)}
              </Tooltip>
            </td>
            <td>
              <OptionsButton
                process={serviceProcess as any}
                setCurrentOpen={open =>
                  setCurrentOpen(open ? serviceProcess.pid : 0)
                }
              />
            </td>
          </tr>
        ))}
      </tbody>
    </S.TableListRoot>
  );
};
